import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Gift } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [accessOffered, setAccessOffered] = useState(false);
  const { clearCart } = useApp();
  const tries = useRef(0);

  useEffect(() => {
    if (params.get("paypal") === "1") { setStatus("paid"); clearCart(); return; }
    const sid = params.get("session_id");
    if (!sid) { setStatus("error"); return; }
    const poll = async () => {
      if (tries.current >= 6) { setStatus("timeout"); return; }
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setStatus("paid"); setAccessOffered(!!data.access_offered); clearCart(); return; }
        if (data.status === "expired") { setStatus("error"); return; }
        setTimeout(poll, 2000);
      } catch { setStatus("error"); }
    };
    poll();
  }, []); // eslint-disable-line

  return (
    <div className="min-h-screen flex items-center justify-center px-6 pt-24" data-testid="payment-success-page">
      <div className="text-center max-w-lg">
        {status === "checking" && (<><Loader2 className="w-14 h-14 text-primary animate-spin mx-auto" /><h1 className="font-serif-display text-4xl mt-8">Confirmation du paiement…</h1></>)}
        {status === "paid" && (<>
          <CheckCircle2 className="w-16 h-16 text-secondary mx-auto" strokeWidth={1.3} />
          <h1 className="font-serif-display text-5xl mt-8 font-light">Merci pour votre commande</h1>
          <p className="text-foreground/70 mt-5 leading-relaxed">Votre paiement a bien été reçu. Nous préparons votre objet avec le plus grand soin et vous contacterons pour la suite.</p>
          {accessOffered && (
            <p data-testid="payment-access-note" className="mt-5 inline-flex items-center gap-2 rounded-xl bg-warm/15 border border-warm/40 px-4 py-3 text-sm text-foreground/80 text-left">
              <Gift className="w-4 h-4 text-warm shrink-0" /> Votre cadeau : l'accès illimité à l'application Passerelle — vos identifiants arrivent dans un second email.
            </p>
          )}
          <Link to="/boutique" className="inline-block mt-8 rounded-full bg-primary text-primary-foreground px-8 py-4 hover:scale-105 transition-transform duration-300">Retour à la boutique</Link>
        </>)}
        {(status === "error" || status === "timeout") && (<>
          <XCircle className="w-16 h-16 text-destructive mx-auto" strokeWidth={1.3} />
          <h1 className="font-serif-display text-4xl mt-8">Un souci est survenu</h1>
          <p className="text-foreground/70 mt-4">{status === "timeout" ? "La confirmation prend plus de temps que prévu. Vérifiez votre email." : "Nous n'avons pas pu confirmer le paiement."}</p>
          <Link to="/boutique" className="inline-block mt-8 rounded-full border border-border px-8 py-4 hover:bg-accent transition-colors">Retour à la boutique</Link>
        </>)}
      </div>
    </div>
  );
};

export const PaymentCancel = () => (
  <div className="min-h-screen flex items-center justify-center px-6 pt-24" data-testid="payment-cancel-page">
    <div className="text-center max-w-lg">
      <XCircle className="w-16 h-16 text-muted-foreground mx-auto" strokeWidth={1.3} />
      <h1 className="font-serif-display text-5xl mt-8 font-light">Paiement annulé</h1>
      <p className="text-foreground/70 mt-5">Votre panier vous attend, vous pouvez reprendre à tout moment.</p>
      <Link to="/boutique" className="inline-block mt-8 rounded-full bg-primary text-primary-foreground px-8 py-4 hover:scale-105 transition-transform duration-300">Revenir à la boutique</Link>
    </div>
  </div>
);
